// ----- Phase phase-ui-dashboard-subrow-sequence-identity -----
//
// One helper for "what IS this sequence?" — the label shown in a dashboard
// sequence sub-row. Sharon, 2026-07-15: sub-rows display the sequence's
// regulatory/submission identity instead of a bare dash.
//
// Where the label comes from, in priority order:
//
//   1. `SubmissionTypeCode` (+ `SubmissionSubTypeCode`) — the structured
//      per-sequence regulatory code. Currently NULL on every row in the DB, but
//      this is the forward-compatible slot: when the activity-model phase
//      (phase-design-submission-requirements-activity-model) lands an
//      ActivityCode, mapping it here is a one-line change.
//   2. `SequenceDescription` — the per-sequence human description. This is what
//      actually varies per sequence TODAY ("Original Application", "Supplement
//      — CMC Update", "Amendment — BE Study", "Annual Report").
//   3. A legacy free-form sequence-level `SubmissionType` (e.g.
//      "nda-original-505b1"). NOTE: a BARE application type (NDA / ANDA / MAA …)
//      is deliberately NOT used — it identifies the APPLICATION, is identical on
//      every sequence, and commit 4bd192f removed it from sub-rows for exactly
//      that reason. Re-printing it here would restore that duplication.
//   4. "Sequence" — never a bare dash.

/** The sequence fields this helper reads. Structural so it stays testable. */
export interface SequenceLike {
  readonly SubmissionType?: string | null;
  readonly SubmissionTypeCode?: string | null;
  readonly SubmissionSubTypeCode?: string | null;
  readonly SequenceDescription?: string | null;
}

/** Known sequence-level regulatory codes → display labels. */
const SEQUENCE_TYPE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  'nda-original-505b1': 'Original (505(b)(1))',
  'nda-original-505b2': 'Original (505(b)(2))',
  'nda-pas': 'PAS Supplement',
  'nda-cbe-30': 'CBE-30 Supplement',
  'nda-cbe-0': 'CBE-0 Supplement',
  'nda-annual-report': 'Annual Report',
  'anda-original': 'Original ANDA',
  'anda-amendment': 'Amendment',
  'ind-original': 'Original IND',
  'ind-amendment': 'IND Amendment',
  'bla-original': 'Original BLA',
  'bla-supplement': 'BLA Supplement',
});

/**
 * APPLICATION-level types. These belong to the whole application and repeat
 * identically on every sequence, so they are never a sequence's identity.
 */
const APPLICATION_TYPES: ReadonlySet<string> = new Set([
  'NDA', 'ANDA', 'BLA', 'IND', 'DMF',
  'MAA', 'J-NDA', 'NDS', 'SNDS', 'CTA', 'ANDS', 'NC', 'NMA', 'MA',
]);

/** Tokens that should stay upper-case when prettifying a raw code. */
const ACRONYMS: ReadonlySet<string> = new Set([
  'NDA', 'ANDA', 'BLA', 'IND', 'DMF', 'MAA', 'NDS', 'PAS', 'CBE', 'PSUR', 'CMC', 'BE',
]);

/** Generic fallback: turn a raw code into something readable. */
function prettify(code: string): string {
  return code
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function mapCode(code: string): string {
  return SEQUENCE_TYPE_LABELS[code.toLowerCase()] ?? prettify(code);
}

/**
 * Human label identifying a single sequence. Never returns an empty string or
 * a bare dash — unknown sequences read as "Sequence".
 */
export function labelForSequence(seq: SequenceLike): string {
  // 1. Structured per-sequence regulatory code (the ActivityCode swap-in point).
  const typeCode = seq.SubmissionTypeCode?.trim();
  if (typeCode) {
    const base = mapCode(typeCode);
    const subCode = seq.SubmissionSubTypeCode?.trim();
    return subCode ? `${base} — ${mapCode(subCode)}` : base;
  }

  // 2. The per-sequence human description — what varies per sequence today.
  const description = seq.SequenceDescription?.trim();
  if (description) return description;

  // 3. Legacy free-form sequence-level SubmissionType. Bare application types
  //    are excluded (see the header note + 4bd192f).
  const submissionType = seq.SubmissionType?.trim();
  if (submissionType && !APPLICATION_TYPES.has(submissionType.toUpperCase())) {
    return mapCode(submissionType);
  }

  // 4. Never a bare dash.
  return 'Sequence';
}
